import { Award, ChevronLeft, ChevronRight, Crown, MapPin, Medal, Star, Trophy } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

interface HallOfFamePerson {
  readonly name: string;
  readonly surname: string | null;
  readonly city: string;
  readonly votes: number;
}

interface CategoryData {
  readonly id: string;
  readonly name: string;
  readonly winner: HallOfFamePerson;
  readonly finalists: readonly [HallOfFamePerson, HallOfFamePerson];
  readonly nominees: readonly HallOfFamePerson[];
}

const CATEGORIES: readonly CategoryData[] = [
  {
    id: 'cardio',
    name: 'Cardiología',
    winner: { name: 'Elena', surname: 'Martínez Ruiz', city: 'Madrid', votes: 1247 },
    finalists: [
      { name: 'Carlos', surname: 'López Fernández', city: 'Barcelona', votes: 1102 },
      { name: 'Ana', surname: 'García Navarro', city: 'Valencia', votes: 983 },
    ],
    nominees: [
      { name: 'Miguel', surname: 'Torres Díaz', city: 'Sevilla', votes: 871 },
      { name: 'Laura', surname: 'Sánchez Moreno', city: 'Bilbao', votes: 804 },
      { name: 'Javier', surname: 'Romero Gil', city: 'Málaga', votes: 756 },
      { name: 'Patricia', surname: 'Hernández Vega', city: 'Zaragoza', votes: 698 },
      { name: 'Daniel', surname: 'Jiménez Castro', city: 'Murcia', votes: 645 },
      { name: 'Sofía', surname: 'Ruiz Blanco', city: 'Palma', votes: 612 },
      { name: 'Alejandro', surname: 'Morales Ortega', city: 'Granada', votes: 589 },
    ],
  },
  {
    id: 'dermato',
    name: 'Dermatología',
    winner: { name: 'Isabel', surname: 'Vega Ortiz', city: 'Sevilla', votes: 1189 },
    finalists: [
      { name: 'Marcos', surname: 'Díaz Luna', city: 'Madrid', votes: 1034 },
      { name: 'Lucía', surname: 'Moreno Campos', city: 'Bilbao', votes: 967 },
    ],
    nominees: [
      { name: 'Ricardo', surname: 'Navarro Sol', city: 'Valencia', votes: 845 },
      { name: 'Carmen', surname: 'Reyes Martín', city: 'Barcelona', votes: 790 },
      { name: 'Pablo', surname: 'Herrera Ruiz', city: 'Málaga', votes: 731 },
      { name: 'Teresa', surname: 'Castro Blanco', city: 'Zaragoza', votes: 680 },
      { name: 'Andrés', surname: 'Gil Serrano', city: 'Murcia', votes: 625 },
      { name: 'Marta', surname: 'Soto Vargas', city: 'Palma', votes: 598 },
      { name: 'Hugo', surname: 'Peña Romero', city: 'Granada', votes: 561 },
    ],
  },
  {
    id: 'neuro',
    name: 'Neurología',
    winner: { name: 'Raúl', surname: 'Serrano Vidal', city: 'Barcelona', votes: 1312 },
    finalists: [
      { name: 'Clara', surname: 'Muñoz Rivas', city: 'Madrid', votes: 1145 },
      { name: 'Diego', surname: 'Ortega Sanz', city: 'Valencia', votes: 1021 },
    ],
    nominees: [
      { name: 'Nuria', surname: 'Pardo León', city: 'Bilbao', votes: 890 },
      { name: 'Álvaro', surname: 'Calvo Mora', city: 'Sevilla', votes: 832 },
      { name: 'Silvia', surname: 'Ramos Fuentes', city: 'Málaga', votes: 778 },
      { name: 'Enrique', surname: 'Vargas Nieto', city: 'Zaragoza', votes: 715 },
      { name: 'Rosa', surname: 'Iglesias Cruz', city: 'Murcia', votes: 667 },
      { name: 'Iván', surname: 'Domínguez Paz', city: 'Palma', votes: 634 },
      { name: 'Beatriz', surname: 'Medina Ruiz', city: 'Granada', votes: 601 },
    ],
  },
  {
    id: 'oftalmo',
    name: 'Oftalmología',
    winner: { name: 'Fernando', surname: 'Blanco Ríos', city: 'Madrid', votes: 1098 },
    finalists: [
      { name: 'Alicia', surname: 'Cano Ferrer', city: 'Barcelona', votes: 978 },
      { name: 'Tomás', surname: 'Nieto Álvarez', city: 'Sevilla', votes: 912 },
    ],
    nominees: [
      { name: 'Eva', surname: 'Prieto Sanz', city: 'Valencia', votes: 834 },
      { name: 'Roberto', surname: 'Luna Gómez', city: 'Bilbao', votes: 776 },
      { name: 'Inés', surname: 'Cruz Molina', city: 'Málaga', votes: 723 },
      { name: 'Manuel', surname: 'Vera Campos', city: 'Zaragoza', votes: 671 },
      { name: 'Lidia', surname: 'Acosta Gil', city: 'Murcia', votes: 618 },
      { name: 'Sergio', surname: 'Pastor Reyes', city: 'Palma', votes: 590 },
      { name: 'Cristina', surname: 'Ruiz Torres', city: 'Granada', votes: 554 },
    ],
  },
  {
    id: 'pediatria',
    name: 'Pediatría',
    winner: { name: 'María', surname: 'Fuentes Lara', city: 'Valencia', votes: 1423 },
    finalists: [
      { name: 'Joaquín', surname: 'Mora Peña', city: 'Madrid', votes: 1201 },
      { name: 'Sandra', surname: 'León Castaño', city: 'Barcelona', votes: 1089 },
    ],
    nominees: [
      { name: 'David', surname: 'Bravo Herrera', city: 'Sevilla', votes: 920 },
      { name: 'Pilar', surname: 'Delgado Vera', city: 'Bilbao', votes: 865 },
      { name: 'Gonzalo', surname: 'Santos Moya', city: 'Málaga', votes: 798 },
      { name: 'Lorena', surname: 'Aguilar Ramos', city: 'Zaragoza', votes: 742 },
      { name: 'Adrián', surname: 'Marín Pardo', city: 'Murcia', votes: 689 },
      { name: 'Paula', surname: 'Ortiz Vega', city: 'Palma', votes: 641 },
      { name: 'Francisco', surname: 'Gallego Cruz', city: 'Granada', votes: 607 },
    ],
  },
  {
    id: 'traumato',
    name: 'Traumatología',
    winner: { name: 'Víctor', surname: 'Ríos Salazar', city: 'Bilbao', votes: 1156 },
    finalists: [
      { name: 'Natalia', surname: 'Guerrero Soto', city: 'Madrid', votes: 1023 },
      { name: 'Óscar', surname: 'Méndez Camacho', city: 'Barcelona', votes: 945 },
    ],
    nominees: [
      { name: 'Sara', surname: 'Benítez Rojas', city: 'Valencia', votes: 856 },
      { name: 'Guillermo', surname: 'Castro Ibáñez', city: 'Sevilla', votes: 789 },
      { name: 'Irene', surname: 'Montero Gil', city: 'Málaga', votes: 734 },
      { name: 'Alberto', surname: 'Peña Crespo', city: 'Zaragoza', votes: 687 },
      { name: 'Julia', surname: 'Vidal Bermejo', city: 'Murcia', votes: 632 },
      { name: 'Luis', surname: 'Serrano Ponce', city: 'Palma', votes: 598 },
      { name: 'Raquel', surname: 'Molina Duarte', city: 'Granada', votes: 567 },
    ],
  },
  {
    id: 'gineco',
    name: 'Ginecología',
    winner: { name: 'Andrea', surname: 'Castaño Leal', city: 'Madrid', votes: 1278 },
    finalists: [
      { name: 'Pedro', surname: 'Rubio Acosta', city: 'Barcelona', votes: 1098 },
      { name: 'Claudia', surname: 'Ferrer Soria', city: 'Valencia', votes: 1012 },
    ],
    nominees: [
      { name: 'Rafael', surname: 'Ibáñez Montes', city: 'Sevilla', votes: 878 },
      { name: 'Diana', surname: 'Crespo Lozano', city: 'Bilbao', votes: 821 },
      { name: 'Rubén', surname: 'Bermejo Salas', city: 'Málaga', votes: 765 },
      { name: 'Helena', surname: 'Moya Santos', city: 'Zaragoza', votes: 710 },
      { name: 'Jorge', surname: 'Ponce Gallego', city: 'Murcia', votes: 659 },
      { name: 'Verónica', surname: 'Duarte León', city: 'Palma', votes: 623 },
      { name: 'Mario', surname: 'Soria Bravo', city: 'Granada', votes: 578 },
    ],
  },
  {
    id: 'onco',
    name: 'Oncología',
    winner: { name: 'Alejandra', surname: 'Pardo Rivas', city: 'Barcelona', votes: 1389 },
    finalists: [
      { name: 'Ignacio', surname: 'Lozano Ávila', city: 'Madrid', votes: 1167 },
      { name: 'Rocío', surname: 'Santos Peña', city: 'Sevilla', votes: 1054 },
    ],
    nominees: [
      { name: 'Adriana', surname: 'Salas Vera', city: 'Valencia', votes: 912 },
      { name: 'Emilio', surname: 'Montes Cruz', city: 'Bilbao', votes: 847 },
      { name: 'Lara', surname: 'Ávila Ortega', city: 'Málaga', votes: 790 },
      { name: 'Nicolás', surname: 'Rojas Pardo', city: 'Zaragoza', votes: 738 },
      { name: 'Valeria', surname: 'Campos Díaz', city: 'Murcia', votes: 684 },
      { name: 'Jaime', surname: 'Herrera Luna', city: 'Palma', votes: 636 },
      { name: 'Noelia', surname: 'Gil Morales', city: 'Granada', votes: 598 },
    ],
  },
  {
    id: 'endocrino',
    name: 'Endocrinología',
    winner: { name: 'Gabriel', surname: 'Molina Vega', city: 'Sevilla', votes: 1045 },
    finalists: [
      { name: 'Esther', surname: 'Cruz Bermejo', city: 'Madrid', votes: 934 },
      { name: 'Marcos', surname: 'Acosta Prieto', city: 'Barcelona', votes: 878 },
    ],
    nominees: [
      { name: 'Blanca', surname: 'Nieto Soto', city: 'Valencia', votes: 798 },
      { name: 'Emilio', surname: 'Calvo Reyes', city: 'Bilbao', votes: 745 },
      { name: 'Ruth', surname: 'Domínguez Lara', city: 'Málaga', votes: 690 },
      { name: 'Samuel', surname: 'Vera Cano', city: 'Zaragoza', votes: 641 },
      { name: 'Elisa', surname: 'Pastor Moreno', city: 'Murcia', votes: 602 },
      { name: 'Félix', surname: 'Ramos Iglesias', city: 'Palma', votes: 567 },
      { name: 'Inma', surname: 'Serrano Aguilar', city: 'Granada', votes: 534 },
    ],
  },
  {
    id: 'uro',
    name: 'Urología',
    winner: { name: 'Lorenzo', surname: 'Delgado Paz', city: 'Madrid', votes: 1134 },
    finalists: [
      { name: 'Olga', surname: 'Bravo Moya', city: 'Barcelona', votes: 987 },
      { name: 'Héctor', surname: 'León Gallego', city: 'Valencia', votes: 921 },
    ],
    nominees: [
      { name: 'Aurora', surname: 'Ibáñez Vera', city: 'Sevilla', votes: 834 },
      { name: 'César', surname: 'Moya Pastor', city: 'Bilbao', votes: 778 },
      { name: 'Gloria', surname: 'Santos Ferrer', city: 'Málaga', votes: 723 },
      { name: 'Ángel', surname: 'Guerrero Sanz', city: 'Zaragoza', votes: 670 },
      { name: 'Nerea', surname: 'Camacho Luna', city: 'Murcia', votes: 621 },
      { name: 'Ramón', surname: 'Soria Calvo', city: 'Palma', votes: 589 },
      { name: 'Dolores', surname: 'Peña Domínguez', city: 'Granada', votes: 551 },
    ],
  },
];

const ANGLE_STEP = 360 / CATEGORIES.length;
const CAROUSEL_RADIUS = 300;

function getInitials(name: string, surname: string | null): string {
  if (!surname) return name.charAt(0).toUpperCase();
  return `${name[0]}${surname[0]}`.toUpperCase();
}

function StarParticles(): React.JSX.Element {
  return (
    <div className="hof-particles" aria-hidden="true">
      {Array.from({ length: 30 }).map((_, i) => (
        <span key={i} className="hof-particles__star" />
      ))}
    </div>
  );
}

function CategoryCarousel({
  activeIndex,
  onSelect,
}: {
  readonly activeIndex: number;
  readonly onSelect: (index: number) => void;
}): React.JSX.Element {
  const handlePrev = useCallback((): void => {
    onSelect((activeIndex - 1 + CATEGORIES.length) % CATEGORIES.length);
  }, [activeIndex, onSelect]);

  const handleNext = useCallback((): void => {
    onSelect((activeIndex + 1) % CATEGORIES.length);
  }, [activeIndex, onSelect]);

  const rotation = -(activeIndex * ANGLE_STEP);

  return (
    <div className="hof-carousel-section">
      <button
        type="button"
        className="hof-carousel-arrow hof-carousel-arrow--left"
        onClick={handlePrev}
        aria-label="Categoría anterior"
      >
        <ChevronLeft size={18} />
      </button>

      <div className="hof-carousel-viewport">
        <div className="hof-carousel-stage">
          <div
            className="hof-carousel-ring"
            style={{
              transform: `rotateY(${rotation}deg)`,
            }}
          >
            {CATEGORIES.map((cat, i) => {
              const angle = i * ANGLE_STEP;
              const isActive = i === activeIndex;
              return (
                <button
                  key={cat.id}
                  type="button"
                  className={`hof-carousel-card ${isActive ? 'hof-carousel-card--active' : ''}`}
                  style={{
                    transform: `rotateY(${angle}deg) translateZ(${CAROUSEL_RADIUS}px)`,
                  }}
                  onClick={() => onSelect(i)}
                  aria-label={`Seleccionar ${cat.name}`}
                  aria-pressed={isActive}
                >
                  <span className="hof-carousel-card__name">{cat.name}</span>
                  {isActive && <span className="hof-carousel-card__glow" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="hof-carousel-reflection" aria-hidden="true" />
      </div>

      <button
        type="button"
        className="hof-carousel-arrow hof-carousel-arrow--right"
        onClick={handleNext}
        aria-label="Categoría siguiente"
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

function WinnerCard({ person }: { readonly person: HallOfFamePerson }): React.JSX.Element {
  return (
    <div className="hof-winner hof-animate hof-animate--delay-2">
      <div className="hof-winner__glow" aria-hidden="true" />
      <div className="hof-winner__crown">
        <Crown size={32} />
      </div>
      <div className="hof-winner__avatar-ring">
        <div className="hof-winner__avatar">{getInitials(person.name, person.surname)}</div>
      </div>
      <div className="hof-winner__badge">
        <Trophy size={14} />
        Ganador
      </div>
      <h2 className="hof-winner__name">
        {[person.name, person.surname].filter(Boolean).join(' ')}
      </h2>
      <p className="hof-winner__city">
        <MapPin size={14} />
        {person.city}
      </p>
      <div className="hof-winner__votes">
        <Star size={16} />
        <span className="hof-winner__votes-count">{person.votes.toLocaleString()}</span>
        <span className="hof-winner__votes-label">votos</span>
      </div>
    </div>
  );
}

function FinalistCard({
  person,
  position,
  delay,
}: {
  readonly person: HallOfFamePerson;
  readonly position: 2 | 3;
  readonly delay: number;
}): React.JSX.Element {
  return (
    <div className={`hof-finalist hof-animate hof-animate--delay-${delay}`}>
      <div className="hof-finalist__rank">
        <Medal size={20} />
        <span>{position}º</span>
      </div>
      <div className="hof-finalist__avatar">{getInitials(person.name, person.surname)}</div>
      <h3 className="hof-finalist__name">
        {[person.name, person.surname].filter(Boolean).join(' ')}
      </h3>
      <p className="hof-finalist__city">
        <MapPin size={12} />
        {person.city}
      </p>
      <div className="hof-finalist__votes">
        <Star size={14} />
        <span>{person.votes.toLocaleString()} votos</span>
      </div>
    </div>
  );
}

function NomineeRow({
  person,
  rank,
  delay,
}: {
  readonly person: HallOfFamePerson;
  readonly rank: number;
  readonly delay: number;
}): React.JSX.Element {
  return (
    <div className={`hof-nominee hof-animate hof-animate--delay-${delay}`}>
      <span className="hof-nominee__rank">#{rank}</span>
      <div className="hof-nominee__avatar">{getInitials(person.name, person.surname)}</div>
      <div className="hof-nominee__info">
        <span className="hof-nominee__name">
          {[person.name, person.surname].filter(Boolean).join(' ')}
        </span>
        <span className="hof-nominee__city">
          <MapPin size={11} />
          {person.city}
        </span>
      </div>
      <div className="hof-nominee__votes">
        <Star size={13} />
        {person.votes.toLocaleString()}
      </div>
    </div>
  );
}

function CategoryResults({ category }: { readonly category: CategoryData }): React.JSX.Element {
  return (
    <div className="hof-results" key={category.id}>
      <div className="hof-podium">
        <div className="hof-podium__side hof-podium__side--left">
          <FinalistCard person={category.finalists[0]} position={2} delay={3} />
        </div>
        <div className="hof-podium__center">
          <WinnerCard person={category.winner} />
        </div>
        <div className="hof-podium__side hof-podium__side--right">
          <FinalistCard person={category.finalists[1]} position={3} delay={4} />
        </div>
      </div>

      <div className="hof-divider hof-animate hof-animate--delay-5">
        <span className="hof-divider__line" />
        <span className="hof-divider__label">Nominados destacados</span>
        <span className="hof-divider__line" />
      </div>

      <div className="hof-nominees-list">
        {category.nominees.map((person, i) => (
          <NomineeRow
            key={`${person.name}-${person.surname ?? i}`}
            person={person}
            rank={i + 4}
            delay={Math.min(i + 5, 10)}
          />
        ))}
      </div>
    </div>
  );
}

export default function HallOfFame(): React.JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeCategory = CATEGORIES[activeIndex];
  if (!activeCategory) return <div className="hof-page" />;

  return (
    <div className="hof-page">
      <StarParticles />

      <nav className="vs-nav">
        <Link to="/voting" className="vs-nav__back">
          Volver
        </Link>
        <span className="vs-nav__brand">
          <span className="vs-nav__logo">
            <Award size={20} />
          </span>
          DP Awards
        </span>
        <div style={{ width: 100 }} />
      </nav>

      <div className="hof-content">
        <div className="hof-hero hof-animate">
          <span className="hof-hero__badge">
            <Trophy size={14} />
            España 2026
          </span>
          <h1 className="hof-hero__title">
            Hall of <span className="hof-hero__title-accent">Fame</span>
          </h1>
          <p className="hof-hero__subtitle">Los profesionales más votados de esta edición</p>
        </div>

        <CategoryCarousel activeIndex={activeIndex} onSelect={setActiveIndex} />

        <div className="hof-active-category hof-animate">
          <h2 className="hof-active-category__name">{activeCategory.name}</h2>
        </div>

        <CategoryResults category={activeCategory} />
      </div>
    </div>
  );
}
